/**
 * DM W8-2 (plan c6412 K2) — the approve-and-execute doors: an `approval` item names one of this closed set and the
 * resolve door runs it as the person (api `dmInboxExecutors.ts` holds the arg schemas and the calls). Danger and
 * effect text are what the confirm dialog shows; the api writes the same text into the receipt.
 */
export const DM_INBOX_EXECUTOR_DOORS = [
    'revision.approve',
    'ac.report',
    'ac.revise',
    'policy.supersede',
    'policy.transition',
    'policy.edit',
    'spec.supersede',
    'doc.transition',
    'item.transition',
    'job.requeue',
    'job.cancel',
    'settings.patch',
    'binding.unbind',
    'item.cap_waive',
    'issue.retier',
    'delegation.grant',
];
export const DM_INBOX_DANGER_LEVELS = ['low', 'medium', 'high'];
export const DM_INBOX_EXECUTOR_DANGER = {
    'revision.approve': 'medium',
    'ac.report': 'medium',
    'ac.revise': 'low',
    'policy.supersede': 'high',
    'policy.transition': 'high',
    'policy.edit': 'medium',
    'spec.supersede': 'high',
    'doc.transition': 'low',
    'item.transition': 'medium',
    'job.requeue': 'low',
    'job.cancel': 'medium',
    'settings.patch': 'high',
    'binding.unbind': 'high',
    'item.cap_waive': 'medium',
    'issue.retier': 'medium',
    'delegation.grant': 'high',
};
/** The caps a person may waive on an item (`item.cap_waive`): only those a guard reads the waiver for (content). */
export const DM_INBOX_CAP_WAIVE_KINDS = ['content'];
function text(value) {
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '?';
}
/** Dotted leaf paths of a settings patch (depth ≤ 3), for the effect line. */
function leafPaths(value, prefix = '', depth = 0) {
    if (value === null || typeof value !== 'object' || Array.isArray(value) || depth >= 3)
        return prefix === '' ? [] : [prefix];
    const keys = Object.keys(value).sort();
    if (keys.length === 0)
        return prefix === '' ? [] : [prefix];
    return keys.flatMap((k) => leafPaths(value[k], prefix === '' ? k : `${prefix}.${k}`, depth + 1));
}
/** One line naming what the door does with these args (read defensively: a malformed arg renders `?`). */
export function dmInboxExecutorEffect(door, args) {
    const item = `itemId=${text(args.itemId)}`;
    switch (door) {
        case 'revision.approve':
            return `approves rev ${text(args.revNo)} of ${item} as its served body (the previous head is superseded)`;
        case 'ac.report':
            return `reports criterion ${text(args.criterionId)} of ${item} as ${text(args.status)}`;
        case 'ac.revise':
            return `rewrites the text of criterion ${text(args.criterionId)} of ${item} (status and evidence kept)`;
        case 'policy.supersede':
            return `publishes version ${Number(args.expectedVersion) + 1 || '?'} of active policy ${item}; the current version retires and its bindings move`;
        case 'policy.transition': {
            const move = `moves policy ${item} from ${text(args.expectedStatus)} to ${text(args.to)}`;
            if (args.to !== 'active')
                return move;
            // #3877 — an activation enforces only where it binds: the line says where, or that it is inert.
            const bind = args.bind !== null && typeof args.bind === 'object' ? args.bind : null;
            return bind !== null
                ? `${move} and binds it to project ${text(bind.targetProjectId)} in the same step`
                : `${move} (unbound — inert until bound)`;
        }
        case 'policy.edit':
            return `edits draft policy ${item} in place`;
        case 'spec.supersede':
            return `makes approved spec ${item} supersede spec itemId=${text(args.supersededItemId)} (the older chain is frozen under it)`;
        case 'doc.transition':
            return `moves doc ${item} from ${text(args.expectedStatus)} to ${text(args.to)}`;
        case 'item.transition':
            return `moves ${item} from ${text(args.expectedStatus)} to ${text(args.to)}`;
        case 'job.requeue':
            return `puts blocked job itemId=${text(args.jobId)} back on the queue (attempts reset)`;
        case 'job.cancel':
            return `cancels job itemId=${text(args.jobId)} (from ${text(args.expectedStatus)})`;
        case 'settings.patch': {
            const paths = leafPaths(args.patch);
            return `changes project ${text(args.projectId)} settings: ${paths.length === 0 ? '?' : paths.join(', ')}`;
        }
        case 'binding.unbind':
            return `revokes policy binding ${text(args.bindingId)}`;
        case 'item.cap_waive':
            return `lifts the ${text(args.cap)} byte cap of plan/spec ${item}: a write over it is admitted with no ceiling (${text(args.value)})`;
        case 'issue.retier':
            return `re-tiers issue ${item} to ${text(args.tier)}`;
        case 'delegation.grant': {
            const uses = typeof args.maxUses === 'number' ? `, ${args.maxUses} use(s)` : '';
            return `lends ${text(args.toolName)} to the operator CLI for ${text(args.ttlMinutes)} min${uses}`;
        }
    }
}
//# sourceMappingURL=dmInboxExecutors.js.map